import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { jobAPI } from '../services/api';

const JobContext = createContext();

const initialState = {
  jobs: [],
  currentJob: null,
  filters: {
    location: '',
    category: '',
    type: '',
    experience: '',
    salary: ''
  },
  loading: false,
  error: null
};

const jobReducer = (state, action) => {
  switch (action.type) {
    case 'SET_JOBS':
      return { ...state, jobs: action.payload, loading: false };
    case 'SET_CURRENT_JOB':
      return { ...state, currentJob: action.payload };
    case 'SET_FILTERS':
      return { ...state, filters: { ...state.filters, ...action.payload } };
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload, loading: false };
    case 'CLEAR_ERROR':
      return { ...state, error: null };
    default:
      return state;
  }
};

export const JobProvider = ({ children }) => {
  const [state, dispatch] = useReducer(jobReducer, initialState);
  const queryClient = useQueryClient();

  // Fetch all jobs
  const { data: jobs, isLoading: jobsLoading, error: jobsError } = useQuery(
    ['jobs', state.filters],
    () => jobAPI.getAllJobs(),
    {
      onSuccess: (data) => {
        dispatch({ type: 'SET_JOBS', payload: data });
      },
      onError: (error) => {
        dispatch({ type: 'SET_ERROR', payload: error.message });
      }
    }
  );

  // Job submission mutation
  const submitJobMutation = useMutation(
    (jobData) => jobAPI.submitJob(jobData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['jobs']);
      },
      onError: (error) => {
        dispatch({ type: 'SET_ERROR', payload: error.message });
      }
    }
  );

  // Job application mutation
  const applyJobMutation = useMutation(
    ({ jobId, applicationData }) => jobAPI.applyForJob(jobId, applicationData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['jobs']);
      },
      onError: (error) => {
        dispatch({ type: 'SET_ERROR', payload: error.message });
      }
    }
  );

  // Update job mutation
  const updateJobMutation = useMutation(
    ({ jobId, jobData }) => jobAPI.updateJob(jobId, jobData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['jobs']);
      },
      onError: (error) => {
        dispatch({ type: 'SET_ERROR', payload: error.message });
      }
    }
  );

  // Delete job mutation
  const deleteJobMutation = useMutation(
    (jobId) => jobAPI.deleteJob(jobId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['jobs']);
      },
      onError: (error) => {
        dispatch({ type: 'SET_ERROR', payload: error.message });
      }
    }
  );

  // Create job mutation
  const createJobMutation = useMutation(
    (jobData) => jobAPI.createJob(jobData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['jobs']);
      },
      onError: (error) => {
        dispatch({ type: 'SET_ERROR', payload: error.message });
      }
    }
  );

  const setFilters = (filters) => {
    dispatch({ type: 'SET_FILTERS', payload: filters });
  };

  const setCurrentJob = (job) => {
    dispatch({ type: 'SET_CURRENT_JOB', payload: job });
  };

  const clearError = () => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  const submitJob = (jobData) => {
    return submitJobMutation.mutateAsync(jobData);
  };

  const applyForJob = (jobId, applicationData) => {
    return applyJobMutation.mutateAsync({ jobId, applicationData });
  };

  const updateJob = (jobId, jobData) => {
    return updateJobMutation.mutateAsync({ jobId, jobData });
  };

  const deleteJob = (jobId) => {
    return deleteJobMutation.mutateAsync(jobId);
  };

  const createJob = (jobData) => {
    return createJobMutation.mutateAsync(jobData);
  };

  const value = {
    ...state,
    jobs: jobs || [],
    loading: jobsLoading,
    error: jobsError?.message || state.error,
    setFilters,
    setCurrentJob,
    clearError,
    submitJob,
    createJob,
    applyForJob,
    updateJob,
    deleteJob,
    submitJobLoading: submitJobMutation.isLoading || createJobMutation.isLoading,
    applyJobLoading: applyJobMutation.isLoading,
    updateJobLoading: updateJobMutation.isLoading,
    deleteJobLoading: deleteJobMutation.isLoading
  };

  return (
    <JobContext.Provider value={value}>
      {children}
    </JobContext.Provider>
  );
};

export const useJob = () => {
  const context = useContext(JobContext);
  if (!context) {
    throw new Error('useJob must be used within a JobProvider');
  }
  return context;
}; 